import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MdExpandMore } from "react-icons/md";

import navigation from "../../../../_nav";
import styles from "./TopNavigation.module.scss";

const isRouteActive = (url, pathname) => {
  if (!url || !pathname) return false;
  if (url === "/") return pathname === "/";
  return pathname === url || pathname.startsWith(`${url}/`);
};

const hasActiveRoute = (items, pathname) =>
  (items || []).some(
    (item) =>
      isRouteActive(item.url, pathname) ||
      hasActiveRoute(item.items, pathname),
  );

const getItemWeight = (item) => {
  if (!item.items?.length) return 1;
  return 1.5 + item.items.reduce((total, child) => total + getItemWeight(child), 0);
};

const distributeIntoColumns = (items, maximumColumns = 3) => {
  const columnCount = Math.min(maximumColumns, Math.max(items.length, 1));
  const columns = Array.from({ length: columnCount }, () => ({
    items: [],
    weight: 0,
  }));

  items.forEach((item) => {
    const targetColumn = columns.reduce((lightest, column) =>
      column.weight < lightest.weight ? column : lightest,
    );
    targetColumn.items.push(item);
    targetColumn.weight += getItemWeight(item);
  });

  return columns;
};

const renderIcon = (Icon) => {
  if (!Icon) return null;
  if (typeof Icon === "function") return <Icon />;
  return React.isValidElement(Icon) ? Icon : null;
};

function MenuItems({ items, pathname, depth = 0, onNavigate }) {
  return (
    <ul className={styles.menuList}>
      {(items || []).map((item) => {
        const hasChildren = item.items?.length > 0;

        if (hasChildren) {
          return (
            <li
              className={`${styles.group} ${depth > 0 ? styles.nestedGroup : ""}`}
              key={item.url || item.name}
            >
              <div className={styles.groupTitle}>
                {depth === 0 && item.icon && (
                  <span className={styles.groupIcon}>
                    {renderIcon(item.icon)}
                  </span>
                )}
                <span>{item.name}</span>
              </div>
              <MenuItems
                items={item.items}
                pathname={pathname}
                depth={depth + 1}
                onNavigate={onNavigate}
              />
            </li>
          );
        }

        return (
          <li key={item.url || item.name}>
            <Link
              className={`${styles.menuLink} ${
                isRouteActive(item.url, pathname) ? styles.activeLink : ""
              }`}
              to={item.url || "#"}
              target={item.target || "_self"}
              onClick={onNavigate}
            >
              <span>{item.name}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export default function TopNavigation({ user }) {
  const [openCategory, setOpenCategory] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ left: 16, arrowLeft: 24 });
  const navigationRef = useRef(null);
  const closeTimerRef = useRef(null);
  const location = useLocation();

  const sections = useMemo(
    () =>
      navigation
        .filter((section) => section.category)
        .map((section) => ({
          ...section,
          items:
            typeof section.items === "function"
              ? section.items(user)
              : section.items || [],
        }))
        .filter((section) => section.items.length > 0),
    [user],
  );

  useEffect(() => {
    setOpenCategory(null);
  }, [location.pathname]);

  const openMenu = (category, triggerElement) => {
    window.clearTimeout(closeTimerRef.current);

    if (triggerElement) {
      const triggerRect = triggerElement.getBoundingClientRect();
      const menuWidth = Math.min(
        window.innerWidth < 1400 ? 680 : 760,
        window.innerWidth - 32,
      );
      const preferredLeft = triggerRect.left - 20;
      const left = Math.max(
        16,
        Math.min(preferredLeft, window.innerWidth - menuWidth - 16),
      );
      const arrowLeft = Math.max(
        24,
        Math.min(triggerRect.left + triggerRect.width / 2 - left, menuWidth - 24),
      );
      setMenuPosition({ left, arrowLeft });
    }

    setOpenCategory(category);
  };

  const scheduleCloseMenu = () => {
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setOpenCategory(null);
    }, 180);
  };

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (
        navigationRef.current &&
        !navigationRef.current.contains(event.target)
      ) {
        setOpenCategory(null);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpenCategory(null);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.clearTimeout(closeTimerRef.current);
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <nav
      className={styles.topNavigation}
      aria-label="Ana menü"
      ref={navigationRef}
    >
      {sections.map((section) => {
        const isOpen = openCategory === section.category;
        const isActive = hasActiveRoute(section.items, location.pathname);
        const columns = distributeIntoColumns(section.items);

        return (
          <div
            className={styles.category}
            key={section.category}
            onMouseEnter={(event) =>
              openMenu(section.category, event.currentTarget)
            }
            onMouseLeave={scheduleCloseMenu}
          >
            <button
              type="button"
              className={`${styles.categoryButton} ${
                isActive ? styles.activeCategory : ""
              }`}
              aria-expanded={isOpen}
              onFocus={(event) =>
                openMenu(section.category, event.currentTarget.parentElement)
              }
            >
              {section.category}
              <MdExpandMore className={isOpen ? styles.rotatedChevron : ""} />
            </button>

            {isOpen && (
              <div
                className={styles.megaMenu}
                style={{
                  left: `${menuPosition.left}px`,
                  "--menu-arrow-left": `${menuPosition.arrowLeft}px`,
                }}
              >
                <div className={styles.megaMenuInner}>
                  <div className={styles.megaMenuHeading}>
                    {section.category}
                  </div>
                  <div className={styles.megaMenuGrid}>
                    {columns.map((column, columnIndex) => (
                      <div className={styles.megaMenuColumn} key={columnIndex}>
                        <MenuItems
                          items={column.items}
                          pathname={location.pathname}
                          onNavigate={() => setOpenCategory(null)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
